<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import { useRoute, useRouter } from 'vue-router'
import { haiTypeTitle, haiTypeColor } from '@/enums/quality/hai-type'
import HaiService from '@/services/quality/hai.service'

import CaseDetailsForm from '@/views/quality/infection-control/hai/forms/CaseDetails.vue'
import CultureDataForm from '@/views/quality/infection-control/hai/forms/CultureData.vue'
import CdcCriteriaForm from '@/views/quality/infection-control/hai/forms/CdcCriteria.vue'
import ReviewForm from '@/views/quality/infection-control/hai/forms/Review.vue'

const { t } = useI18n()

definePage({
  meta: {
    subject: 'quality.infection_control.hai_case',
    action: 'create',
  },
})

const route = useRoute()
const router = useRouter()

const snackbar = useToast()
const unitId = cookieRef('selectedUnit')

const currentStep = ref(0)
const isCurrentStepValid = ref(true)
const isSubmitting = ref(false)
const showSummary = ref(false)

const steps = [
  { title: 'Case Details' },
  { title: 'Culture & Organism' },
  { title: 'CDC Criteria' },
  { title: 'Review' },
]

const caseDetailsForm = ref({})
const cultureDataForm = ref({})
const cdcCriteriaForm = ref({})
const criteriaCount = ref(0)

// Live data mirrors for real-time summary (doesn't feed back into child props)
const liveCaseData = ref({})
const liveCultureDataRef = ref({})
const liveCdcData = ref({})

const allFormData = computed(() => ({
  ...caseDetailsForm.value,
  ...cultureDataForm.value,
  ...cdcCriteriaForm.value,
  ...liveCaseData.value,
  ...liveCultureDataRef.value,
  ...liveCdcData.value,
}))

// CDC status label
const cdcMetCount = computed(() => (allFormData.value.cdc_criteria_met ?? []).filter(c => c.met).length)

const cdcTotalCount = computed(() => criteriaCount.value)

const cdcStatusLabel = computed(() => {
  if (!allFormData.value.hai_type) return 'Not assessed'
  if (cdcMetCount.value === 0) return 'Not assessed'
  if (cdcMetCount.value === cdcTotalCount.value) return 'Fully met'

  return 'Partially met'
})

const cdcStatusColor = computed(() => {
  if (cdcMetCount.value === 0) return 'secondary'
  if (cdcMetCount.value === cdcTotalCount.value) return 'success'

  return 'warning'
})

// Summary data
const summaryPatientName = computed(() => {
  return allFormData.value.patient_name
    || allFormData.value.patient?.name
    || '—'
})

const summaryOrganism = computed(() => {
  return allFormData.value.organism?.name ?? '—'
})

const summaryLocation = computed(() => {
  return allFormData.value.location?.name ?? '—'
})

const summaryDeviceTypeLabel = computed(() => {
  const labels = { CLABSI: 'Central line type', CAUTI: 'Catheter type', VAP: 'Ventilator type' }

  return labels[allFormData.value.hai_type] || 'Device type'
})

const summaryDeviceType = computed(() => {
  return allFormData.value.device_type ?? 'Not set'
})

// Load existing data for edit mode
if (route.params.id) {
  await useApi(createUrl(`/infection-control/hai-cases/${route.params.id}`, {}), {
    afterFetch: ({ data }) => {
      const {
        cdc_criteria_met,
        organism_id,
        secondary_organism_id,
        infection_site_id,
        culture_date,
        culture_result,
        sensitivity_data,
        is_mdro,
        wound_class,
        ...caseFields
      } = data

      caseDetailsForm.value = caseFields

      cultureDataForm.value = {
        hai_type: caseFields.hai_type,
        culture_date,
        organism_id,
        secondary_organism_id,
        infection_site_id,
        culture_result,
        sensitivity_data,
        is_mdro,
        wound_class,
      }

      cdcCriteriaForm.value = {
        hai_type: caseFields.hai_type,
        cdc_criteria_met: cdc_criteria_met ?? [],
      }
    },
    onFetchError: () => {
      router.push({ name: 'invalid-url', params: { code: 404 } })
    },
  })
}

const updateCurrentStep = value => {
  currentStep.value = value
}

// Live change handlers for real-time summary (write to separate refs to avoid prop feedback loop)
const liveCaseDetails = formData => {
  liveCaseData.value = formData
}

const liveCultureDataHandler = formData => {
  liveCultureDataRef.value = formData
}

const liveCdcCriteria = formData => {
  liveCdcData.value = formData
}

const handleCriteriaCount = count => {
  criteriaCount.value = count
}

const submitCaseDetails = async formData => {
  caseDetailsForm.value = formData
  liveCaseData.value = {}

  cultureDataForm.value = {
    ...cultureDataForm.value,
    hai_type: formData.hai_type,
  }

  cdcCriteriaForm.value = {
    ...cdcCriteriaForm.value,
    hai_type: formData.hai_type,
  }

  currentStep.value++
}

const submitCultureData = formData => {
  cultureDataForm.value = formData
  liveCultureDataRef.value = {}
  currentStep.value++
}

const submitCdcCriteria = formData => {
  cdcCriteriaForm.value = formData
  liveCdcData.value = {}
  currentStep.value++
}

const submitReview = async () => {
  isSubmitting.value = true

  const payload = {
    ...JSON.parse(JSON.stringify(allFormData.value)),
    unit_id: unitId.value,
  }

  if (route.params.id) {
    await HaiService.update(route.params.id, payload, () => {
      router.push({ name: 'app-quality-infection-control-hai' })
    }, () => {
      isSubmitting.value = false
    })
  } else {
    await HaiService.create(payload, () => {
      router.push({ name: 'app-quality-infection-control-hai' })
    }, () => {
      isSubmitting.value = false
    })
  }

  isSubmitting.value = false
}
</script>

<template>
  <!-- Stepper -->
  <div class="mb-6">
    <AppStepper
      v-model:current-step="currentStep"
      :items="steps"
      :is-active-step-valid="isCurrentStepValid"
    />
  </div>

  <!-- Top Bar: HAI Type + Status + Case Summary Toggle -->
  <VCard
    v-if="allFormData.hai_type"
    variant="flat"
    class="border mb-4"
  >
    <VCardText class="d-flex align-center justify-space-between py-3">
      <div class="d-flex align-center gap-3">
        <VChip
          :color="haiTypeColor(allFormData.hai_type)"
          size="small"
          label
        >
          {{ allFormData.hai_type }}
        </VChip>
        <span
          class="text-body-2 font-weight-medium"
          :class="cdcMetCount === cdcTotalCount && cdcTotalCount > 0 ? 'text-success' : 'text-medium-emphasis'"
        >
          {{ cdcStatusLabel === 'Fully met' ? 'Meets definition' : cdcStatusLabel }}
        </span>
      </div>
      <VBtn
        variant="text"
        size="small"
        :append-icon="showSummary ? 'tabler-chevron-up' : 'tabler-chevron-down'"
        @click="showSummary = !showSummary"
      >
        <span class="d-none d-sm-inline">Case summary</span>
        <span class="d-inline d-sm-none">Summary</span>
      </VBtn>
    </VCardText>

    <!-- Collapsible Case Summary -->
    <VExpandTransition>
      <div v-show="showSummary">
        <VCardText class="py-0">
          <!-- CDC Progress Bar -->
          <VProgressLinear
            v-if="cdcTotalCount > 0"
            :model-value="(cdcMetCount / cdcTotalCount) * 100"
            :color="cdcStatusColor"
            height="6"
          />

          <div class="py-3">
            <VRow dense>
              <VCol
                cols="12"
                sm="6"
                class="d-flex"
              >
                <span class="summary-label text-body-2 text-medium-emphasis">{{ t('Customer') }}</span>
                <span class="text-body-2 font-weight-medium">{{ summaryPatientName }}</span>
              </VCol>
              <VCol
                cols="12"
                sm="6"
                class="d-flex"
              >
                <span class="summary-label text-body-2 text-medium-emphasis">Ward</span>
                <span class="text-body-2 font-weight-medium">{{ summaryLocation }}</span>
              </VCol>
            </VRow>
            <VRow dense>
              <VCol
                cols="12"
                sm="6"
                class="d-flex"
              >
                <span class="summary-label text-body-2 text-medium-emphasis">Onset</span>
                <span class="text-body-2 font-weight-medium">{{ allFormData.infection_onset_date ?? '—' }}</span>
              </VCol>
              <VCol
                cols="12"
                sm="6"
                class="d-flex"
              >
                <span class="summary-label text-body-2 text-medium-emphasis">{{ summaryDeviceTypeLabel }}</span>
                <span
                  class="text-body-2 font-weight-medium"
                  :class="allFormData.device_type ? '' : 'text-error'"
                >
                  {{ summaryDeviceType }}
                </span>
              </VCol>
            </VRow>
            <VRow dense>
              <VCol
                cols="12"
                sm="6"
                class="d-flex"
              >
                <span class="summary-label text-body-2 text-medium-emphasis">Organism</span>
                <span class="text-body-2 font-weight-medium">{{ summaryOrganism }}</span>
              </VCol>
              <VCol
                cols="12"
                sm="6"
                class="d-flex"
              >
                <span class="summary-label text-body-2 text-medium-emphasis">MDRO</span>
                <span class="text-body-2 font-weight-medium">{{ allFormData.is_mdro ? 'Yes' : 'No' }}</span>
              </VCol>
            </VRow>
          </div>
        </VCardText>
      </div>
    </VExpandTransition>
  </VCard>

  <!-- Step Content -->
  <VWindow
    v-model="currentStep"
    class="disable-tab-transition"
  >
    <!-- Step 1: Case Details -->
    <VWindowItem class="bg-var-theme-white">
      <CaseDetailsForm
        :data="caseDetailsForm"
        :current-step="currentStep"
        @update:form-data="submitCaseDetails"
        @update:current-step="updateCurrentStep"
        @live-change="liveCaseDetails"
      />
    </VWindowItem>

    <!-- Step 2: Culture & Organism -->
    <VWindowItem class="bg-var-theme-white">
      <CultureDataForm
        :data="cultureDataForm"
        :current-step="currentStep"
        @update:form-data="submitCultureData"
        @update:current-step="updateCurrentStep"
        @live-change="liveCultureDataHandler"
      />
    </VWindowItem>

    <!-- Step 3: CDC Criteria -->
    <VWindowItem class="bg-var-theme-white">
      <CdcCriteriaForm
        :data="cdcCriteriaForm"
        :current-step="currentStep"
        @update:form-data="submitCdcCriteria"
        @update:current-step="updateCurrentStep"
        @live-change="liveCdcCriteria"
        @criteria-count="handleCriteriaCount"
      />
    </VWindowItem>

    <!-- Step 4: Review -->
    <VWindowItem class="bg-var-theme-white">
      <ReviewForm
        :data="allFormData"
        :current-step="currentStep"
        :is-submitting="isSubmitting"
        :cdc-met-count="cdcMetCount"
        :cdc-total-count="cdcTotalCount"
        @update:submit="submitReview"
        @update:current-step="updateCurrentStep"
      />
    </VWindowItem>
  </VWindow>
</template>

<style scoped>
.summary-label {
  inline-size: 120px;
  flex-shrink: 0;
}
</style>
